import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, MapPin, Mail, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { useSettings } from '../../context/SettingsContext';

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'Tours', path: '/tours' },
  { name: 'About', path: '/about' },
  { name: 'Gallery', path: '/gallery' },
  { name: 'Contact', path: '/contact' },
];

export function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const { settings, loading } = useSettings();

  const companyInfo = settings?.company_info || {};
  const phone = companyInfo.phone || '+91 9181268900';
  const email = companyInfo.email || 'nyravtravels@gmail.com';
  const companyName = companyInfo.name || 'NYRAV TOURS AND TRAVELS';
  const address = companyInfo.address || 'Guwahati, India';
  // Show full address
  const shortLocation = address;

  // Handle scroll for sticky header effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    const timer = setTimeout(() => setIsOpen(false), 0);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  return (
    <header 
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
        isScrolled ? "shadow-lg" : ""
      )}
      data-testid="site-header"
    >
      {/* Top Bar - Hidden on scroll for mobile */}
      <div 
        className={cn(
          "bg-[#004E64] text-white py-2 px-4 transition-all duration-300",
          isScrolled ? "hidden md:block md:py-1.5" : "hidden md:block"
        )}
      >
        <div className="max-w-7xl mx-auto flex justify-between items-center text-sm">
          <div className="flex items-center gap-6">
            <a href={`tel:${phone.replace(/\s/g, '')}`} className="flex items-center gap-2 hover:text-[#FF6B35] transition-colors">
              <Phone className="w-4 h-4" />
              {phone}
            </a>
            <a href={`mailto:${email}`} className="flex items-center gap-2 hover:text-[#FF6B35] transition-colors">
              <Mail className="w-4 h-4" />
              {email}
            </a>
            <span className="flex items-center gap-2 hidden lg:flex">
              <MapPin className="w-4 h-4" />
              {shortLocation}
            </span>
          </div>
          <Link 
            to="/admin/login" 
            className="hover:text-[#FF6B35] transition-colors"
            data-testid="admin-login-link"
          >
            Admin Login
          </Link>
        </div>
      </div>

      {/* Main Navigation */}
      <nav 
        className={cn(
          "transition-all duration-300",
          isScrolled 
            ? "bg-white/95 backdrop-blur-md py-3 shadow-md" 
            : "bg-white/80 backdrop-blur-sm py-4"
        )}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex justify-between items-center">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-3 group" 
            data-testid="logo-link"
          >
            {/* Logo Image or Default Circle */}
            {settings.logo ? (
              <img 
                src={settings.logo} 
                alt={companyName}
                className="h-12 md:h-14 lg:h-16 w-auto object-contain"
              />
            ) : (
              <div className="w-12 h-12 md:w-14 md:h-14 bg-gradient-to-br from-[#FF6B35] to-[#E85A2D] rounded-full flex items-center justify-center shadow-md group-hover:shadow-lg transition-shadow">
                <span className="text-white font-bold text-xl md:text-2xl">{companyName[0]}</span>
              </div>
            )}
            
            {/* Company Name - Always Show */}
            <div>
              <span 
                className="text-xl md:text-2xl font-bold text-[#004E64] tracking-wide" 
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                {companyName.split(' ')[0]}
              </span>
              <span className="text-xs md:text-sm block text-[#4A4A4A] -mt-1 tracking-widest">
                {companyName.split(' ').slice(1).join(' ') || 'TOURS & TRAVELS'}
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.name.toLowerCase()}`}
                className={cn(
                  "px-5 py-2.5 font-semibold text-[16px] rounded-lg transition-all duration-200 uppercase tracking-wide",
                  location.pathname === link.path 
                    ? "text-white bg-[#FF6B35] shadow-md" 
                    : "text-[#004E64] hover:text-white hover:bg-[#FF6B35] hover:shadow-md"
                )}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:block">
            <Link to="/contact">
              <Button 
                data-testid="nav-get-quote-btn"
                className="bg-gradient-to-r from-[#FF6B35] to-[#E85A2D] hover:from-[#E85A2D] hover:to-[#D4491D] text-white rounded-full px-6 py-2 font-semibold shadow-md hover:shadow-lg transition-all duration-200"
              >
                Get Quote
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-btn"
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle menu"
          >
            {isOpen ? (
              <X className="w-6 h-6 text-[#004E64]" />
            ) : (
              <Menu className="w-6 h-6 text-[#004E64]" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        <div 
          className={cn(
            "lg:hidden absolute top-full left-0 right-0 bg-white border-t shadow-xl transition-all duration-300 overflow-hidden",
            isOpen ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
          )}
        >
          <div className="py-4 px-4 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                className={cn(
                  "block py-3 px-4 font-semibold text-[16px] rounded-lg transition-colors uppercase tracking-wide",
                  location.pathname === link.path 
                    ? "text-white bg-[#FF6B35]" 
                    : "text-[#004E64] hover:bg-[#FF6B35]/10"
                )}
                onClick={() => setIsOpen(false)}
              >
                {link.name}
              </Link>
            ))}
            
            {/* Mobile CTA */}
            <div className="pt-4 px-4">
              <Link to="/contact" onClick={() => setIsOpen(false)}>
                <Button 
                  className="w-full bg-gradient-to-r from-[#FF6B35] to-[#E85A2D] text-white rounded-full font-semibold"
                  data-testid="mobile-get-quote-btn"
                >
                  Get Quote
                </Button>
              </Link>
            </div>

            {/* Mobile Contact Info */}
            <div className="pt-4 px-4 border-t mt-4 space-y-3 text-sm text-gray-600">
              <a href={`tel:${phone.replace(/\s/g, '')}`} className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-[#FF6B35]" />
                {phone}
              </a>
              <a href={`mailto:${email}`} className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-[#FF6B35]" />
                {email}
              </a>
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
}
